/**
 * React-Native-Web social-login popup driver.
 *
 * On the web build `react-native-webview` has no usable implementation, so the
 * WebView authorization-code flow (`SocialLoginWebView`) breaks. Instead we
 * mirror the admin panel: open a real browser popup (`window.open`) to the
 * provider authorize URL, let it redirect to the backend relay
 * (`<api>/auth/<provider>/callback`), and receive the result via `postMessage`.
 *
 * The backend relay (`renderOAuthPopupRelay`) decodes the opener origin from the
 * `state` — a base64url JSON `{o:<origin>, n:<nonce>}` — and only then posts the
 * `{__happyOAuth:true, code, id_token, access_token, state, error}` message back
 * to that exact origin (never `*`). It is also what makes Facebook/GitHub serve
 * the relay page at all (`isPopupRelayState`). So we MUST send that state shape
 * and our page origin MUST be on the backend allowlist (happygastro.hu /
 * happysolutions.hu / happypos.hu / happytrade.hu subdomains, or localhost).
 */

import { buildAuthorizeUrl } from './socialOAuth';
import { ApiConfig } from '../config/ApiConfig';

/** The origin the relay page posts FROM (the API host). */
const API_ORIGIN = (() => {
    try {
        return new window.URL(ApiConfig.apiURL).origin;
    } catch (_e) {
        return 'https://api.happysolutions.hu';
    }
})();

/** base64url-encode a JS object (UTF-8 safe), matching the backend decoder. */
function base64Url(obj) {
    const json = JSON.stringify(obj);
    const b64 = window.btoa(unescape(encodeURIComponent(json)));

    return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Build the `{o,n}` popup state the backend relay decodes for the opener origin. */
function makeWebState() {
    const nonce = `${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;

    return base64Url({ o: window.location.origin, n: nonce });
}

/**
 * Run a provider OAuth popup on web and resolve the credential to POST to
 * `/auth/social`. MUST be called synchronously from the user's tap so the
 * `window.open` is not treated as a blocked popup.
 *
 * @param {string} provider microsoft | facebook | github | linkedin
 * @returns {Promise<{provider:string, code:?string, idToken:?string, accessToken:?string, redirectUri:string}>}
 * @throws {Error} `popup_blocked` | `cancelled` | `state_mismatch` | provider error code
 */
export function runWebOAuthPopup(provider) {
    return new Promise((resolve, reject) => {
        if (typeof window === 'undefined' || typeof window.open !== 'function') {
            reject(new Error('web_unsupported'));

            return;
        }

        let built;

        try {
            built = buildAuthorizeUrl(provider, makeWebState());
        } catch (e) {
            reject(e);

            return;
        }

        const { url, redirectUri, state } = built;
        const w = 600;
        const h = 720;
        const dualLeft = window.screenX != null ? window.screenX : (window.screenLeft || 0);
        const dualTop = window.screenY != null ? window.screenY : (window.screenTop || 0);
        const docEl = window.document && window.document.documentElement;
        const vw = window.innerWidth || (docEl && docEl.clientWidth) || 1024;
        const vh = window.innerHeight || (docEl && docEl.clientHeight) || 768;
        const left = dualLeft + Math.max(0, (vw - w) / 2);
        const top = dualTop + Math.max(0, (vh - h) / 2);

        // Open synchronously (inside the tap) so the browser doesn't block it.
        const popup = window.open(url, `happy_oauth_${provider}`, `width=${w},height=${h},left=${left},top=${top}`);

        if (!popup) {
            reject(new Error('popup_blocked'));

            return;
        }

        let settled = false;

        const cleanup = () => {
            settled = true;
            window.removeEventListener('message', onMessage);
            window.clearInterval(poll);
            window.clearTimeout(timer);
        };

        const onMessage = (event) => {
            if (settled) {
                return;
            }
            // The relay posts from the API origin and targets our origin. Accept
            // either to be safe, then gate hard on the message signature + state.
            if (event.origin !== API_ORIGIN && event.origin !== window.location.origin) {
                return;
            }
            const d = event.data;

            if (!d || d.__happyOAuth !== true) {
                return;
            }
            if (d.state && state && d.state !== state) {
                return;
            }
            cleanup();
            try {
                popup.close();
            } catch (_e) {
                // ignore — user may have closed it already
            }
            if (d.error) {
                reject(new Error(d.error));

                return;
            }
            resolve({
                provider,
                code: d.code || null,
                idToken: d.id_token || null,
                accessToken: d.access_token || null,
                redirectUri,
            });
        };

        window.addEventListener('message', onMessage);

        // If the user closes the popup without finishing, resolve as cancelled.
        const poll = window.setInterval(() => {
            if (settled) {
                return;
            }
            if (popup.closed) {
                cleanup();
                reject(new Error('cancelled'));
            }
        }, 500);

        // Safety valve so a hung provider tab can't leak the listener forever.
        const timer = window.setTimeout(() => {
            if (settled) {
                return;
            }
            cleanup();
            try {
                popup.close();
            } catch (_e) {
                // ignore
            }
            reject(new Error('timeout'));
        }, 5 * 60 * 1000);
    });
}
